import { useEffect, useState } from "react";
import api, { formatApiError, num } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin, Loader2, Wand2, Download, Archive, Radar, CheckCircle2, X, Sparkles, Mail } from "lucide-react";

export default function ProspectAssignmentPanel() {
  const [summary, setSummary] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [dir, setDir] = useState(null);
  const [enrich, setEnrich] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [geo, setGeo] = useState(null);
  const [geoRun, setGeoRun] = useState(null);
  const [applyingGeo, setApplyingGeo] = useState(false);
  const [autoRun, setAutoRun] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewStato, setReviewStato] = useState("giallo");
  const [reviewRows, setReviewRows] = useState(null);

  const loadSummary = () => api.get("/prospects/assignment-summary").then((r) => setSummary(r.data)).catch(() => {});
  const loadDir = () => api.get("/prospects/directional-breakdown").then((r) => setDir(r.data)).catch(() => {});
  const loadGeo = () => api.get("/prospects/geo-analysis/summary").then((r) => setGeo(r.data)).catch(() => {});
  const pollGeo = () => {
    const t = setInterval(async () => {
      try {
        const r = await api.get("/prospects/geo-analysis/status");
        setGeoRun(r.data);
        if (!r.data.running) {
          clearInterval(t);
          if (r.data.total > 0) toast.success(`Analisi completata: ${r.data.verde} validati, ${r.data.giallo} da verificare, ${r.data.rosso} non identificati`);
          loadGeo();
        }
      } catch { /* ignore */ }
    }, 3000);
  };
  const pollEnrich = () => {
    const t = setInterval(async () => {
      try {
        const r = await api.get("/prospects/enrich-directional/status");
        setEnrich(r.data);
        if (!r.data.running) {
          clearInterval(t);
          if (r.data.total > 0) toast.success(`Arricchimento completato: ${r.data.enriched} localizzati, ${r.data.assigned} assegnati ad agenzia`);
          loadSummary(); loadDir();
        }
      } catch { /* ignore */ }
    }, 3000);
  };
  const pollAuto = () => {
    const t = setInterval(async () => {
      try {
        const r = await api.get("/prospects/auto-process-directional/status");
        setAutoRun(r.data);
        if (!r.data.running) {
          clearInterval(t);
          if (r.data.finished) toast.success(`Automazione completata: ${r.data.assigned} assegnati, ${r.data.newsletter_added} aggiunti in rubrica newsletter, ${r.data.archived} archiviati`);
          loadSummary(); loadDir();
        }
      } catch { /* ignore */ }
    }, 3000);
  };
  useEffect(() => {
    loadSummary();
    loadDir();
    loadGeo();
    api.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {});
    api.get("/prospects/enrich-directional/status").then((r) => { setEnrich(r.data); if (r.data.running) pollEnrich(); }).catch(() => {});
    api.get("/prospects/auto-process-directional/status").then((r) => { setAutoRun(r.data); if (r.data.running) pollAuto(); }).catch(() => {});
    api.get("/prospects/geo-analysis/status").then((r) => { setGeoRun(r.data); if (r.data.running) pollGeo(); }).catch(() => {});
  }, []);

  const startEnrich = async () => {
    try {
      await api.post("/prospects/enrich-directional");
      toast.success("Arricchimento avviato: recupero provincia/regione da CAP e città.");
      setEnrich({ running: true, processed: 0, total: 0, enriched: 0, assigned: 0 });
      pollEnrich();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Avvio non riuscito"); }
  };
  const exportContacts = async () => {
    try {
      const r = await api.get("/prospects/directional-contacts", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "prospect_direzionali_contatti.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export non riuscito"); }
  };
  const syncNewsletter = async () => {
    try {
      const r = await api.post("/prospects/directional-to-newsletter");
      toast.success(`${num(r.data.added)} email aggiunte alla rubrica newsletter (categoria Prospect (rubrica))`);
      loadDir();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
  };
  const startAuto = async () => {    if (!window.confirm("Automatizzare l'intero processo sui prospect direzionali?\n\n1) Arricchisce gli indirizzi (CAP/città → provincia/regione → agenzia in zona)\n2) Inserisce tutte le email in rubrica newsletter (categoria 'Prospect')\n3) Archivia i prospect senza alcun dato utile\n\nGira in background e rispetta disiscritti/blocklist.")) return;
    try {
      await api.post("/prospects/auto-process-directional", { archive: true });
      toast.success("Automazione avviata in background.");
      setAutoRun({ running: true, step: "arricchimento", newsletter_added: 0, enriched: 0, assigned: 0, archived: 0 });
      pollAuto();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Avvio non riuscito"); }
  };
  const archiveUnusable = async () => {    try {
      const drun = await api.post("/prospects/archive-unusable", { dry_run: true });
      if (!drun.data.count) { toast.info("Nessun prospect inutilizzabile da archiviare."); return; }
      if (!window.confirm(`Archiviare ${drun.data.count} prospect senza localizzazione né contatto? Verranno nascosti da liste, mappa e conteggi (recuperabili in seguito).`)) return;
      setArchiving(true);
      const r = await api.post("/prospects/archive-unusable", { dry_run: false });
      toast.success(`Archiviati ${r.data.applied} prospect inutilizzabili`);
      loadSummary(); loadDir();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Archiviazione non riuscita"); }
    finally { setArchiving(false); }
  };

  const startGeoAnalysis = async () => {
    if (!window.confirm("Avviare l'analisi di geolocalizzazione (P.IVA + ragione sociale via Creditsafe)? Consuma crediti Creditsafe e gira in background.")) return;
    try {
      await api.post("/prospects/geo-analysis/start");
      toast.success("Analisi avviata in background.");
      setGeoRun({ running: true, processed: 0, total: 0, verde: 0, giallo: 0, rosso: 0, bianco: 0 });
      pollGeo();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Avvio non riuscito"); }
  };
  const applyValidated = async () => {
    if (!geo?.verde) { toast.info("Nessun risultato validato da applicare."); return; }
    if (!window.confirm(`Aggiornare automaticamente ${geo.verde} prospect validati (indirizzo, coordinate e assegnazione per territorio)?`)) return;
    setApplyingGeo(true);
    try {
      const r = await api.post("/prospects/geo-analysis/apply-validated");
      toast.success(`Aggiornati ${r.data.applied} prospect (${r.data.assigned} assegnati ad agenzia)`);
      loadGeo(); loadSummary(); loadDir();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aggiornamento non riuscito"); }
    finally { setApplyingGeo(false); }
  };
  const openReview = async (stato) => {
    setReviewStato(stato); setReviewOpen(true); setReviewRows(null);
    try { const r = await api.get(`/prospects/geo-analysis/review?stato=${encodeURIComponent(stato)}`); setReviewRows(r.data); }
    catch { toast.error("Impossibile caricare la coda di revisione"); setReviewRows([]); }
  };
  const applyOne = async (pid) => {
    try {
      await api.post("/prospects/geo-analysis/apply-one", { prospect_id: pid });
      toast.success("Prospect aggiornato");
      setReviewRows((rows) => (rows || []).filter((x) => x.prospect_id !== pid));
      loadGeo(); loadSummary(); loadDir();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Applicazione non riuscita"); }
  };
  const dismissOne = async (pid) => {
    try {
      await api.post("/prospects/geo-analysis/dismiss-one", { prospect_id: pid });
      setReviewRows((rows) => (rows || []).filter((x) => x.prospect_id !== pid));
      loadGeo();
    } catch { toast.error("Operazione non riuscita"); }
  };

  const openPreview = async () => {
    try { const r = await api.post("/prospects/reassign-territory", { dry_run: true }); setPreview(r.data); setOverrides({}); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/prospects/reassign-territory", { dry_run: false, overrides });
      toast.success(`Riassegnati ${r.data.applied} prospect`);
      setPreview(null); setOverrides({}); loadSummary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const fixInvalid = async () => {
    setFixing(true);
    try {
      const dr = await api.post("/prospects/fix-invalid-agency", { dry_run: true });
      const { invalid_total, ricollegati, per_territorio, a_direzionale } = dr.data;
      if (!invalid_total) { toast.info("Nessuna assegnazione non valida da correggere."); return; }
      if (!window.confirm(`${invalid_total} prospect con agenzia inesistente:\n• ${ricollegati} ricollegati all'agenzia corretta\n• ${per_territorio} riassegnati per territorio\n• ${a_direzionale} messi direzionali\nProcedere?`)) return;
      const r = await api.post("/prospects/fix-invalid-agency", { dry_run: false });
      toast.success(`Corrette ${r.data.applied} assegnazioni`);
      loadSummary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFixing(false); }
  };

  if (!summary) return null;
  return (
    <Card className="p-6" data-testid="assignment-summary-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Assegnazione prospect per agenzia</h2></div>
        <Button variant="outline" size="sm" onClick={openPreview} data-testid="reassign-territory-btn"><MapPin className="mr-2 h-4 w-4" /> Ricalcola assegnazione per territorio</Button>
      </div>
      <div className="mb-4 flex flex-wrap gap-3 text-sm">
        <div className="rounded-md border border-border px-3 py-2"><span className="text-xs text-muted-foreground">Totale prospect</span><div className="font-heading text-lg font-bold" data-testid="assign-total">{num(summary.total)}</div></div>
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2"><span className="text-xs text-muted-foreground">Direzionali (senza agenzia)</span><div className="font-heading text-lg font-bold text-amber-400" data-testid="assign-direzionali">{num(summary.direzionali)}</div></div>
      </div>
      {summary.by_source && summary.by_source.length > 0 && (
        <div className="mb-4" data-testid="assign-by-source">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prospect per fonte</div>
          <div className="flex flex-wrap gap-2">
            {summary.by_source.map((s) => (
              <div key={s.source} className="rounded-md border border-border px-3 py-2" data-testid={`source-chip-${s.source}`}>
                <div className="text-[11px] text-muted-foreground">{s.source}</div>
                <div className="font-heading text-base font-bold">{num(s.count)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="max-h-72 overflow-y-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead className="text-right">Prospect assegnati</TableHead></TableRow></TableHeader>
          <TableBody>
            {summary.per_agency.map((r) => (
              <TableRow key={r.agency_id} data-testid={`assign-row-${r.agency_id}`}>
                <TableCell className="font-medium">{r.agency_name}</TableCell>
                <TableCell className="text-right font-mono">{num(r.count)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="font-semibold text-amber-400">Direzionali (no agenzia)</TableCell>
              <TableCell className="text-right font-mono text-amber-400">{num(summary.direzionali)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      {summary.invalid && summary.invalid.length > 0 && (
        <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 p-3" data-testid="assign-invalid">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-red-400">⚠ Assegnazioni non valide (agenzia inesistente): {num(summary.invalid_total)} prospect</div>
            <Button variant="outline" size="sm" onClick={fixInvalid} disabled={fixing} data-testid="fix-invalid-btn">{fixing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Correggi assegnazioni non valide</Button>
          </div>
          <div className="space-y-1 text-xs">
            {summary.invalid.map((r) => (
              <div key={r.agency_id} className="flex justify-between gap-2"><span className="truncate text-muted-foreground">{r.guess} <span className="opacity-60">({r.agency_id})</span></span><span className="font-mono">{num(r.count)}</span></div>
            ))}
          </div>
        </div>
      )}

      {dir && dir.total > 0 && (
        <div className="mt-4 rounded-md border border-border p-4" data-testid="directional-manager">
          <div className="mb-2 flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-sm font-bold">Gestione prospect direzionali</h3>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">I {num(dir.total)} prospect senza agenzia, suddivisi per cosa manca. Con <b>Automatizza tutto</b> vengono arricchiti gli indirizzi, le email finiscono in rubrica newsletter (categoria <b>Prospect (rubrica)</b>) e i record senza alcun dato vengono archiviati — nel rispetto di disiscritti e blocklist. Il processo gira anche in automatico a ogni import prospect e ogni notte.</p>
          <div className="mb-3 grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
            <div className="rounded-md border border-border px-3 py-2" data-testid="dir-territorio"><div className="text-[11px] text-muted-foreground">Con provincia/regione (serve agenzia in zona)</div><div className="font-heading text-base font-bold">{num(dir.con_territorio)}</div></div>
            <div className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2" data-testid="dir-arricchibili"><div className="text-[11px] text-muted-foreground">Arricchibili (CAP/città → territorio)</div><div className="font-heading text-base font-bold text-sky-400">{num(dir.arricchibili)}</div></div>
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2" data-testid="dir-contatto"><div className="text-[11px] text-muted-foreground">Solo contatto (email/telefono)</div><div className="font-heading text-base font-bold text-emerald-400">{num(dir.solo_contatto)}</div></div>
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2" data-testid="dir-inutilizzabili"><div className="text-[11px] text-muted-foreground">Inutilizzabili (nessun dato)</div><div className="font-heading text-base font-bold text-red-400">{num(dir.inutilizzabili)}</div></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={startAuto} disabled={autoRun?.running || enrich?.running} data-testid="auto-process-directional-btn">
              {autoRun?.running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Automatizza tutto
            </Button>
            <Button variant="outline" size="sm" onClick={startEnrich} disabled={enrich?.running || autoRun?.running || dir.arricchibili === 0} data-testid="enrich-directional-btn">
              {enrich?.running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />} Arricchisci indirizzi ({num(dir.arricchibili)})
            </Button>
            <Button variant="outline" size="sm" onClick={syncNewsletter} disabled={autoRun?.running || dir.con_email === 0} data-testid="sync-newsletter-btn"><Mail className="mr-2 h-4 w-4" /> Aggiungi a rubrica newsletter ({num(dir.con_email)})</Button>
            <Button variant="outline" size="sm" onClick={exportContacts} disabled={(dir.con_email + dir.con_telefono) === 0} data-testid="export-contacts-btn"><Download className="mr-2 h-4 w-4" /> Esporta lista telemarketing</Button>
            <Button variant="outline" size="sm" onClick={archiveUnusable} disabled={archiving || autoRun?.running || dir.inutilizzabili === 0} className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="archive-unusable-btn">{archiving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Archive className="mr-2 h-4 w-4" />} Archivia inutilizzabili ({num(dir.inutilizzabili)})</Button>
          </div>
          {autoRun?.running && (
            <div className="mt-3" data-testid="auto-progress">
              <div className="mb-1 text-[11px] text-muted-foreground">Automazione in corso — fase: <b>{autoRun.step}</b> · localizzati {num(autoRun.enriched)} · assegnati {num(autoRun.assigned)} · in rubrica {num(autoRun.newsletter_added)} · archiviati {num(autoRun.archived)}</div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full w-1/2 animate-pulse rounded-full bg-primary" /></div>
            </div>
          )}
          {enrich?.running && (
            <div className="mt-3" data-testid="enrich-progress">
              <div className="mb-1 text-[11px] text-muted-foreground">Arricchimento in corso… {num(enrich.processed)}/{num(enrich.total)} · localizzati {num(enrich.enriched)} · assegnati {num(enrich.assigned)}</div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${enrich.total ? Math.round((enrich.processed / enrich.total) * 100) : 0}%` }} /></div>
            </div>
          )}
        </div>
      )}

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="geo-review-dialog">
          <DialogHeader><DialogTitle>Revisione casi dubbi</DialogTitle></DialogHeader>
          {!reviewRows ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Caricamento…</div>
          ) : reviewRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Nessun caso da revisionare.</p>
          ) : (
            <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
              {reviewRows.map((r) => (
                <div key={r.prospect_id} className="rounded-md border border-white/10 bg-black/30 p-3" data-testid={`review-row-${r.prospect_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <span className={r.stato === "giallo" ? "text-amber-400" : r.stato === "bianco" ? "text-muted-foreground" : "text-red-400"}>{r.stato === "giallo" ? "🟡" : r.stato === "bianco" ? "⚪" : "🔴"}</span>
                        <span className="truncate">{r.name}</span>
                        {r.confidence != null && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px]">conf. {r.confidence}%</span>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {r.found_name && r.found_name !== r.name ? <span>Trovato: <b>{r.found_name}</b> · </span> : null}
                        {r.address ? <span>{r.address}, </span> : null}{r.cap ? r.cap + " " : ""}{r.city || ""}{r.province ? ` (${r.province})` : ""}{r.region ? ` · ${r.region}` : ""}
                        {!r.address && !r.city ? <span className="text-red-400">Nessun indirizzo trovato</span> : null}
                        {r.source ? <span className="opacity-60"> · {r.source}</span> : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {(r.province || r.city) && <Button size="sm" variant="outline" onClick={() => applyOne(r.prospect_id)} className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" data-testid={`review-apply-${r.prospect_id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Applica</Button>}
                      <Button size="sm" variant="ghost" onClick={() => dismissOne(r.prospect_id)} className="text-muted-foreground" data-testid={`review-dismiss-${r.prospect_id}`}><X className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader><DialogTitle>Ricalcola assegnazione per territorio</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">Vengono rielaborati <b>tutti</b> i prospect in base a provincia/regione. Per i territori senza agenzia associata, scegli tu a chi assegnarli (o lasciali direzionali).</p>
              <div className="flex flex-wrap gap-3">
                <div className="rounded-md border border-border px-3 py-2"><span className="text-xs text-muted-foreground">Prospect totali</span><div className="font-heading text-lg font-bold" data-testid="reassign-candidates">{num(preview.candidates)}</div></div>
                <div className="rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2"><span className="text-xs text-muted-foreground">Assegnati per territorio</span><div className="font-heading text-lg font-bold text-green-400" data-testid="reassign-assignable">{num(preview.assignable)}</div></div>
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2"><span className="text-xs text-muted-foreground">Senza dato territorio (restano direzionali)</span><div className="font-heading text-lg font-bold text-amber-400" data-testid="reassign-still">{num(preview.senza_territorio)}</div></div>
              </div>
              {preview.unresolved && preview.unresolved.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-400">Territori senza agenzia — scegli a chi assegnarli ({preview.pending_unresolved} prospect)</div>
                  <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border border-border p-2">
                    {preview.unresolved.map((u) => (
                      <div key={u.key} className="flex items-center justify-between gap-2" data-testid={`unresolved-${u.key}`}>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{u.province || u.region || "—"}</div>
                          <div className="text-[11px] text-muted-foreground">{u.region && u.province ? u.region + " · " : ""}{num(u.count)} prospect</div>
                        </div>
                        <Select value={overrides[u.key] || "direzionale"} onValueChange={(v) => setOverrides((s) => ({ ...s, [u.key]: v }))}>
                          <SelectTrigger className="h-8 w-[190px]" data-testid={`unresolved-select-${u.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="direzionale">Mantieni direzionale</SelectItem>
                            {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {preview.per_agency.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-md border border-border">
                  <Table>
                    <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead className="text-right">Prospect (dopo ricalcolo)</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {preview.per_agency.map((r) => (
                        <TableRow key={r.agency_id}><TableCell>{r.agency_name}</TableCell><TableCell className="text-right font-mono">{num(r.count)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="reassign-cancel">Annulla</Button>
            <Button onClick={confirm} disabled={busy} data-testid="reassign-confirm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MapPin className="mr-2 h-4 w-4" />} Conferma e assegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
